/* oxlint-disable jsx-a11y/prefer-tag-over-role */
import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { useTemplateDesignerContext } from '../template-designer-context';
import styles from './styles.module.less';
import { WIDGET_TYPE } from '../widgets/widget-type';

import type { Widget } from '../config';

enum ResizeDirection {
  Top = 'top',
  Right = 'right',
  Bottom = 'bottom',
  Left = 'left',
}

type WidgetResizerProps = {
  widget: Widget;
  children: React.ReactNode;
  minWidth?: number;
  maxWidth: number;
  minHeight?: number;
  maxHeight: number;
};

export const WidgetDragResize = ({ widget, minWidth = 12, maxWidth, minHeight = 12, maxHeight, children }: WidgetResizerProps) => {
  const { onClick, updateWidget, useSnapToGrid, moveStepSize, isSelectedWidget, isTransformingRef, onTransform } =
    useTemplateDesignerContext();

  const isSelected = isSelectedWidget(widget.id);
  const [isResizing, setIsResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [resizeDirection, setResizeDirection] = useState<ResizeDirection | null>(null);

  const dragStartRef = useRef({
    clientX: 0,
    clientY: 0,
    widgetX: widget.x,
    widgetY: widget.y,
    widgetWidth: widget.width,
    widgetHeight: widget.height,
  });

  const [transformerWidget, setTransformerWidget] = useState({
    id: widget.id,
    x: widget.x,
    y: widget.y,
    width: widget.width,
    height: widget.height,
  });

  // Dimensions can also be updated from another level - the details panel.
  // Therefore, if there is a change in them, we need to update the state of the transformer
  useEffect(() => {
    setTransformerWidget({
      id: widget.id,
      x: widget.x,
      y: widget.y,
      width: widget.width,
      height: widget.height,
    });
  }, [widget.height, widget.id, widget.width, widget.x, widget.y]);

  useEffect(() => {
    const handler = setTimeout(() => {
      // Only update if the transformer widget is different from the original widget
      // This prevents unnecessary history entries when a widget is first added
      const hasChanged =
        transformerWidget.x !== widget.x ||
        transformerWidget.y !== widget.y ||
        transformerWidget.width !== widget.width ||
        transformerWidget.height !== widget.height;

      if (hasChanged) {
        updateWidget(transformerWidget);
      }
    }, 200);

    return () => {
      clearTimeout(handler);
    };
  }, [updateWidget, transformerWidget, widget.x, widget.y, widget.width, widget.height]);

  const onResizeMouseDown = (e: React.MouseEvent<HTMLDivElement>, direction: ResizeDirection | null) => {
    dragStartRef.current = {
      clientX: e.clientX,
      clientY: e.clientY,
      widgetX: transformerWidget.x,
      widgetY: transformerWidget.y,
      widgetWidth: transformerWidget.width,
      widgetHeight: transformerWidget.height,
    };

    onTransform(true);
    setResizeDirection(direction);
    setIsResizing(true);
    e.stopPropagation();
    e.preventDefault();
  };

  const onDragMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (!isSelected) {
      return;
    }

    dragStartRef.current = {
      clientX: e.clientX,
      clientY: e.clientY,
      widgetX: transformerWidget.x,
      widgetY: transformerWidget.y,
      widgetWidth: transformerWidget.width,
      widgetHeight: transformerWidget.height,
    };

    onTransform(true);
    setIsDragging(true);
  };

  useEffect(() => {
    const handleResize = (e: globalThis.MouseEvent) => {
      if (!resizeDirection) {
        return;
      }

      // Safety check: if mouse buttons are released, stop resizing
      if (e.buttons === 0) {
        setIsResizing(false);
        return;
      }

      e.stopPropagation();

      let newWidth = transformerWidget.width;
      let newHeight = transformerWidget.height;
      let newX = transformerWidget.x;
      let newY = transformerWidget.y;

      if (useSnapToGrid) {
        const deltaX = e.clientX - dragStartRef.current.clientX;
        const deltaY = e.clientY - dragStartRef.current.clientY;

        const stepsX = Math.round(deltaX / moveStepSize) * moveStepSize;
        const stepsY = Math.round(deltaY / moveStepSize) * moveStepSize;

        switch (resizeDirection) {
          case ResizeDirection.Top:
            newY = dragStartRef.current.widgetY + stepsY;
            newHeight = dragStartRef.current.widgetHeight - stepsY;
            break;
          case ResizeDirection.Left:
            newX = dragStartRef.current.widgetX + stepsX;
            newWidth = dragStartRef.current.widgetWidth - stepsX;
            break;
          case ResizeDirection.Right:
            newWidth = dragStartRef.current.widgetWidth + stepsX;
            break;
          case ResizeDirection.Bottom:
            newHeight = dragStartRef.current.widgetHeight + stepsY;
            break;
          default:
            return;
        }
      } else {
        switch (resizeDirection) {
          case ResizeDirection.Top:
            newY = transformerWidget.y + e.movementY;
            newHeight = transformerWidget.height - e.movementY;
            break;
          case ResizeDirection.Left:
            newX = transformerWidget.x + e.movementX;
            newWidth = transformerWidget.width - e.movementX;
            break;
          case ResizeDirection.Right:
            newWidth = transformerWidget.width + e.movementX;
            break;
          case ResizeDirection.Bottom:
            newHeight = transformerWidget.height + e.movementY;
            break;
          default:
            return;
        }
      }

      const availableWidth = maxWidth - newX;
      const availableHeight = maxHeight - newY;
      const minX = 0;
      const minY = 0;

      if (minX > newX) {
        newX = minX;
      }
      if (minY > newY) {
        newY = minY;
      }
      if (availableHeight < newHeight) {
        newHeight = availableHeight;
      }
      if (availableWidth < newWidth) {
        newWidth = availableWidth;
      }
      if (newWidth < minWidth) {
        newWidth = minWidth;
      }
      if (newHeight < minHeight) {
        newHeight = minHeight;
      }

      setTransformerWidget((prev) => ({
        ...prev,
        width: newWidth,
        height: newHeight,
        x: newX,
        y: newY,
      }));
    };

    const handleDrag = (e: globalThis.MouseEvent) => {
      // Safety check: if mouse buttons are released, stop dragging
      if (e.buttons === 0) {
        setIsDragging(false);
        return;
      }

      let newX = transformerWidget.x;
      let newY = transformerWidget.y;

      if (useSnapToGrid) {
        const deltaX = e.clientX - dragStartRef.current.clientX;
        const deltaY = e.clientY - dragStartRef.current.clientY;

        const stepsX = Math.round(deltaX / moveStepSize) * moveStepSize;
        const stepsY = Math.round(deltaY / moveStepSize) * moveStepSize;

        newX = dragStartRef.current.widgetX + stepsX;
        newY = dragStartRef.current.widgetY + stepsY;
      } else {
        newX = newX + e.movementX;
        newY = newY + e.movementY;
      }

      const minX = 0;
      const minY = 0;
      const maxX = maxWidth - transformerWidget.width;
      const maxY = maxHeight - transformerWidget.height;

      if (newX < minX) {
        newX = minX;
      }
      if (newY < minY) {
        newY = minY;
      }
      if (newX > maxX) {
        newX = maxX;
      }
      if (newY > maxY) {
        newY = maxY;
      }

      setTransformerWidget((prev) => ({
        ...prev,
        x: newX,
        y: newY,
      }));
    };

    const onMouseMove = (e: globalThis.MouseEvent) => {
      // Check if mouse buttons are still pressed - if not, end drag/resize
      if (e.buttons === 0) {
        setIsResizing(false);
        setIsDragging(false);
        return;
      }

      if (isResizing) {
        handleResize(e);
      } else if (isDragging) {
        handleDrag(e);
      }
    };

    const onMouseUp = () => {
      setIsResizing(false);
      setIsDragging(false);

      // Event order critical for preventing unwanted selection changes:
      // 1. mouseup → document listener (capture phase) executes this onMouseUp
      // 2. mouseup → onClick handler - may fire if browser considers this a click
      //
      // Without setTimeout: onTransform(false) executes synchronously, so onClick
      // sees isTransformingRef.current = false and incorrectly changes selection.
      //
      // With setTimeout: Defers onTransform(false) to next event loop tick. This ensures
      // onClick (which runs in the same tick as mouseup) still sees isTransformingRef.current = true,
      // correctly blocking selection change during drag/resize operations.
      setTimeout(() => {
        onTransform(false);
      }, 0);
    };

    document.addEventListener('mousemove', onMouseMove, true);
    document.addEventListener('mouseup', onMouseUp, true);

    return () => {
      document.removeEventListener('mousemove', onMouseMove, true);
      document.removeEventListener('mouseup', onMouseUp, true);
    };
  }, [
    isResizing,
    isDragging,
    resizeDirection,
    transformerWidget.height,
    transformerWidget.width,
    transformerWidget.x,
    transformerWidget.y,
    maxWidth,
    maxHeight,
    minHeight,
    minWidth,
    moveStepSize,
    useSnapToGrid,
    onTransform,
  ]);

  return (
    <div
      aria-pressed={isSelected}
      className={classNames(styles['dragger-resizer'], {
        [styles['dragger-resizer--selected']]: isSelected,
      })}
      data-testid={`dragger-resizer-${widget.id}`}
      onClick={(e) => {
        e.stopPropagation();

        // Widget selection during resize/drag fix:
        //
        // DOM Structure example:
        //   Row WidgetDragResize (parent)
        //     └─ Group div
        //         └─ Text WidgetDragResize (nested child)
        //             └─ Resize anchor (bottom)
        //
        // Problem: When resizing nested widget, cursor moves outside child boundaries
        // and hovers over parent Row. When user releases mouseup over Row, browser
        // generates onClick on Row element, incorrectly changing selection from child to Row.
        //
        // Solution: Global isTransformingRef in context tracks if ANY widget is being
        // transformed (dragged/resized). Set to true on drag/resize start, false on end.
        // Check ref here before calling onClick - prevents selection change during transform.
        if (!isTransformingRef.current) {
          onClick(widget.id);
        }
      }}
      onMouseDown={onDragMouseDown}
      onMouseUp={(e) => {
        e.stopPropagation();
      }}
      role='button'
      style={{
        top: transformerWidget.y,
        left: transformerWidget.x,
        width: transformerWidget.width,
        height: transformerWidget.height,
        cursor: isSelected ? 'grab' : 'pointer',
      }}
      tabIndex={0}
    >
      {isSelected ? (
        <>
          {widget.type !== WIDGET_TYPE.row && (
            <div
              className={styles['anchor-left']}
              data-testid='anchor-left'
              onMouseDown={(e) => onResizeMouseDown(e, ResizeDirection.Left)}
              role='button'
              tabIndex={0}
            />
          )}
          {widget.type !== WIDGET_TYPE.row && (
            <div
              className={styles['anchor-right']}
              data-testid='anchor-right'
              onMouseDown={(e) => onResizeMouseDown(e, ResizeDirection.Right)}
              role='button'
              tabIndex={0}
            />
          )}
          <div
            className={styles['anchor-top']}
            data-testid='anchor-top'
            onMouseDown={(e) => onResizeMouseDown(e, ResizeDirection.Top)}
            role='button'
            tabIndex={0}
          />
          <div
            className={styles['anchor-bottom']}
            data-testid='anchor-bottom'
            onMouseDown={(e) => onResizeMouseDown(e, ResizeDirection.Bottom)}
            role='button'
            tabIndex={0}
          />
        </>
      ) : null}
      {children}
    </div>
  );
};
